/**
 * Groups what is wrong across every account the viewer can reach.
 *
 * No timespan, and no traffic or revenue. Health reports on experiments that
 * are running right now, and none of its signals are windowed: a broken split
 * is broken, an experiment past its end date is late, a dead one is dead. A
 * date picker over that would imply the page re-derives per period when it does
 * not. Volume over a period is what Performance and the dashboard are for.
 *
 * Dropping the traffic columns also takes dashboard_stats off this path, so the
 * page stays correct when the aggregation cron is behind.
 *
 * Pure, so the grouping is testable without a database.
 */

export type HealthSeverity = 'broken' | 'decide' | 'queue';

/** Enough to render a row and to search it. */
export type HealthItem = {
  kind: string;
  severity: HealthSeverity;
  experimentId: number;
  experimentName: string;
  data: Record<string, unknown>;
};

export type WebsiteRow = {
  websiteId: number;
  url: string;
  companyId: number;
  runningCount: number;
  items: HealthItem[];
};

export type ClientTotals = {
  runningCount: number;
  brokenCount: number;
  decideCount: number;
  queueCount: number;
  itemCount: number;
};

export type ClientGroup = {
  companyId: number;
  companyName: string;
  websites: WebsiteRow[];
  totals: ClientTotals;
};

export type HealthRollup = {
  clients: ClientGroup[];
  totals: {
    clientCount: number;
    websiteCount: number;
    runningCount: number;
    brokenCount: number;
    decideCount: number;
    queueCount: number;
  };
};

const countBy = (items: readonly HealthItem[], severity: HealthSeverity) =>
  items.filter((item) => item.severity === severity).length;

function totalsFor(websites: readonly WebsiteRow[]): ClientTotals {
  const items = websites.flatMap((website) => website.items);
  return {
    runningCount: websites.reduce((sum, website) => sum + website.runningCount, 0),
    brokenCount: countBy(items, 'broken'),
    decideCount: countBy(items, 'decide'),
    queueCount: countBy(items, 'queue'),
    itemCount: items.length,
  };
}

/**
 * Ordered by what needs attention, not alphabetically.
 *
 * Someone opens this to find out which account to look at first, so the one
 * with a broken experiment sorts above the one with none. Name is the final
 * tiebreak so the order is stable between refreshes.
 */
function compareClients(a: ClientGroup, b: ClientGroup): number {
  if (a.totals.brokenCount !== b.totals.brokenCount) return b.totals.brokenCount - a.totals.brokenCount;
  if (a.totals.decideCount !== b.totals.decideCount) return b.totals.decideCount - a.totals.decideCount;
  if (a.totals.queueCount !== b.totals.queueCount) return b.totals.queueCount - a.totals.queueCount;
  if (a.totals.runningCount !== b.totals.runningCount) return b.totals.runningCount - a.totals.runningCount;
  return a.companyName.localeCompare(b.companyName);
}

type BuildRollupParams = {
  clients: readonly { id: number; name: string }[];
  websites: readonly WebsiteRow[];
};

export function buildHealthRollup({ clients, websites }: BuildRollupParams): HealthRollup {
  const byCompany = new Map<number, WebsiteRow[]>();
  for (const website of websites) {
    const existing = byCompany.get(website.companyId);
    if (existing) existing.push(website);
    else byCompany.set(website.companyId, [website]);
  }

  const groups: ClientGroup[] = clients.map((client) => {
    const rows = (byCompany.get(client.id) ?? []).slice().sort((a, b) => a.url.localeCompare(b.url));
    return { companyId: client.id, companyName: client.name, websites: rows, totals: totalsFor(rows) };
  });

  groups.sort(compareClients);

  return {
    clients: groups,
    totals: {
      clientCount: groups.length,
      websiteCount: groups.reduce((sum, group) => sum + group.websites.length, 0),
      runningCount: groups.reduce((sum, group) => sum + group.totals.runningCount, 0),
      brokenCount: groups.reduce((sum, group) => sum + group.totals.brokenCount, 0),
      decideCount: groups.reduce((sum, group) => sum + group.totals.decideCount, 0),
      queueCount: groups.reduce((sum, group) => sum + group.totals.queueCount, 0),
    },
  };
}

/**
 * Free-text filter across the three things someone looks up: an account, a
 * website, or an experiment.
 *
 * A match on the account or website name keeps that whole subtree, because the
 * intent there is "show me this client". A match on an experiment name narrows
 * to the websites carrying it, so searching for one test does not drag in every
 * sibling under the same account.
 */
export function filterRollup(rollup: HealthRollup, query: string): HealthRollup {
  const needle = query.trim().toLowerCase();
  if (!needle) return rollup;

  const clients: ClientGroup[] = [];

  for (const client of rollup.clients) {
    if (client.companyName.toLowerCase().includes(needle)) {
      clients.push(client);
      continue;
    }

    const websites: WebsiteRow[] = [];
    for (const website of client.websites) {
      if (website.url.toLowerCase().includes(needle)) {
        websites.push(website);
        continue;
      }
      const items = website.items.filter((item) => item.experimentName.toLowerCase().includes(needle));
      if (items.length) websites.push({ ...website, items });
    }

    if (websites.length) clients.push({ ...client, websites, totals: totalsFor(websites) });
  }

  return {
    clients,
    totals: {
      clientCount: clients.length,
      websiteCount: clients.reduce((sum, client) => sum + client.websites.length, 0),
      runningCount: clients.reduce((sum, client) => sum + client.totals.runningCount, 0),
      brokenCount: clients.reduce((sum, client) => sum + client.totals.brokenCount, 0),
      decideCount: clients.reduce((sum, client) => sum + client.totals.decideCount, 0),
      queueCount: clients.reduce((sum, client) => sum + client.totals.queueCount, 0),
    },
  };
}
